import {
  Alert,
  Box,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { type LinksFunction } from 'react-router';
import api from '~/api/Endpoints';
import { ChatGptPluginCta } from '~/components/ChatGptPluginCta';
import { Link } from '~/components/Link';
import { PageTemplate } from '~/components/PageTemplate';
import { useQuery } from '~/hooks/useQuery';
import { useUser } from '~/hooks/useUser';
import { ApiFreeTierCard } from '~/scenes/api/ApiFreeTierCard';
import { ApiKeyTab } from '~/scenes/api/ApiKeyTab';
import { ApiPlans } from '~/scenes/api/ApiPlans';
import { ApiTutorials } from '~/scenes/api/ApiTutorials';
import stylesheet from '~/scenes/api/styles.css?url';
import { SwaggerDocs } from '~/scenes/api/SwaggerDocs';
import { FakeTerminal } from '~/scenes/mcp/FakeTerminal';
import { getPageMetaTags } from '~/scripts/getPageMetaTags';
import { useHandleException } from '~/scripts/handleException';
import useDataLayer from '~/services/datalayer';
import type { GetApiAccessResponse } from '@shared/types/api-access';
import type { Route } from './+types/api';
import { useTabs } from '~/hooks/useTabs';

export const meta: Route.MetaFunction = () => {
  return getPageMetaTags({
    title: 'API Access',
    description:
      "Integrate NerdyData.com's API into your software or application",
    canonical: 'https://www.nerdydata.com/api',
    faq: [
      {
        question: 'What can I do with the NerdyData API?',
        answer:
          'The NerdyData API lets you search millions of websites by technology, code snippet, title, or meta description. You can retrieve company info, email addresses, LinkedIn profiles, and estimated ad spend for matching domains — all programmatically.',
      },
      {
        question: 'How do I get an API key?',
        answer:
          'Sign up for a free NerdyData account and navigate to the API page. Every account includes free monthly API and MCP credits. Add pay-as-you-go billing to continue beyond them.',
      },
      {
        question: 'What formats does the API return?',
        answer: 'The NerdyData API returns JSON responses.',
      },
      {
        question: 'Is there API documentation?',
        answer:
          'Yes. Interactive Swagger documentation is available directly on the API page at nerdydata.com/api, where you can explore endpoints and test requests in your browser.',
      },
      {
        question: 'Does NerdyData have an MCP?',
        answer:
          'Yes. NerdyData now supports MCP to find websites by their technology or code - directly in Cursor, Claude, and other LLMs. Learn more about MCP <a href="https://www.nerdydata.com/mcp">here</a>.',
      },
    ],
  });
};

export const links: LinksFunction = () => [
  {
    rel: 'stylesheet',
    href: stylesheet,
  },
];

export default function ApiPage({}: Route.ComponentProps) {
  const [loading, setLoading] = useState(false);
  const [provisionedApiAccess, setProvisionedApiAccess] = useState(false);
  const user = useUser();
  const dataLayer = useDataLayer();
  const handleException = useHandleException();
  const { tabs, activeTab, onTabsChange, tabsRef } = useTabs([
    {
      hash: 'apikey',
      label: 'API Key',
    },
    {
      hash: 'pricing',
      label: 'Pricing',
    },
    {
      hash: 'documentation',
      label: 'Documentation',
    },
    {
      hash: 'tutorials',
      label: 'Tutorials',
    },
  ]);

  const getApiAccessFunction = useCallback(async () => {
    if (user?.isGuest) {
      return { api_key: null, chargeable: false };
    }
    setLoading(true);
    const { api_key, chargeable, pay_as_you_go, credits } = await api
      .get('/api-access')
      .finally(() => {
        setLoading(false);
      });
    return { api_key, chargeable, pay_as_you_go, credits };
  }, [user?.isGuest]);

  const apiAccessRequest = useQuery<GetApiAccessResponse, AxiosError>({
    queryKey: [user?.uuid || 'guest', provisionedApiAccess],
    queryFn: getApiAccessFunction,
    onError(error) {
      handleException(error, { category: 'api-access' });
    },
  });

  useEffect(() => {
    // provision pay-as-you-go after a payment method is added
    if (
      apiAccessRequest.data?.chargeable === true &&
      apiAccessRequest.data?.pay_as_you_go === false
    ) {
      setLoading(true);
      dataLayer.push({
        event: 'api.subscribe',
      });

      api
        .put('/api-access')
        .then(() => {
          setProvisionedApiAccess(true);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    apiAccessRequest.data?.pay_as_you_go,
    apiAccessRequest.data?.chargeable,
    setLoading,
    setProvisionedApiAccess,
    dataLayer,
  ]);

  const isLoading = loading || apiAccessRequest.isLoading;

  return (
    <>
      <PageTemplate
        breadcrumbs={[
          { href: '/', text: 'Home' },
          { href: '/integrations', text: 'Integrations' },
          { href: '/api', text: 'API Access', disabled: true },
        ]}
        secondaryRow={
          <Container sx={{ pt: 4 }}>
            <ApiFreeTierCard />
          </Container>
        }
        title="API Access"
        subtitle="Automate searches and domain lookups, and only pay for what you use."
        description="Search Code, Technology Lists, Domain Lookups, and more. Enrich large lists of domains with tech stack and company information, or monitor prospect domains for changes to their technographics.">
        <Grid container justifyContent="center" sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <ChatGptPluginCta />
            </Box>
            <Alert severity="info" sx={{ maxWidth: 740, mx: 'auto', mb: 2 }}>
              NerdyData now supports MCP - use it directly in Cursor, Claude,
              and other LLMs.{' '}
              <Link href="/mcp" color="inherit" underline="always">
                Learn more
              </Link>
            </Alert>
            <Link href="/mcp" underline="none">
              <FakeTerminal />
            </Link>
          </Grid>
        </Grid>
        <Box sx={{ mt: 4 }}>
          <Tabs
            ref={tabsRef}
            value={activeTab}
            onChange={onTabsChange}
            sx={{
              scrollMarginTop: 80,
              borderBottom: 1,
              borderColor: 'divider',
              mb: 3,
            }}>
            {tabs.map((tab) => {
              return <Tab key={tab.hash} label={tab.label} />;
            })}
          </Tabs>
          {activeTab === 0 && (
            <ApiKeyTab apiAccess={apiAccessRequest.data} loading={isLoading} />
          )}
          {activeTab === 1 && (
            <Grid container spacing={4} justifyContent={'center'}>
              <ApiPlans />
              <Grid sm={12} md={12} lg={8} item>
                <Typography variant="body1" textAlign={'center'}>
                  Every account includes free monthly API and MCP credits.
                  Pay-as-you-go pricing only applies once those are used up.
                  Only successful requests (200 status) are counted towards your
                  usage. A $5.00 flat fee is applied upon your first billable
                  paid API tier&apos;s monthly usage.
                  <br />
                  <br />
                  In general, you can use our API to enhance your product or
                  research in many ways. The only limitation is you cannot
                  resell the data as-is or provide competitive/duplicate
                  functionality to nerdydata.com and our associated services.
                </Typography>
              </Grid>
            </Grid>
          )}
          {activeTab === 2 && (
            <Grid container justifyContent={'center'}>
              <Grid item sm={12} textAlign={'center'}>
                <SwaggerDocs />
              </Grid>
            </Grid>
          )}
          {activeTab === 3 && <ApiTutorials />}
        </Box>
      </PageTemplate>
    </>
  );
}
